/**
 * LPCSpriteRenderer - Renders modular LPC (Liberated Pixel Cup) sprite sheets.
 *
 * Supports TWO sprite sheet formats:
 *
 * Standard (832×1344, 64×64 frames, 13 cols × 21 rows):
 *   Row 0-3:   Spellcast (7 frames) — up, left, down, right
 *   Row 4-7:   Thrust    (8 frames)
 *   Row 8-11:  Walk      (9 frames)
 *   Row 12-15: Slash     (6 frames)
 *   Row 16-19: Shoot    (13 frames)
 *   Row 20:    Hurt      (6 frames, down only)
 *
 * Oversized (1152×768, 192×192 frames, 6 cols × 4 rows):
 *   Slash-only animation for large weapons (longsword, mace, etc.)
 *   Row 0=up, 1=left, 2=down, 3=right, 6 frames each
 *   Center 64×64 is extracted to composite with standard body layers.
 *   Non-slash animations fall back to a standard-format weapon sprite.
 *
 * Supports compositing multiple layers (body, armor, weapon, hair, helmet)
 * and caching the composited result per unique combination.
 */

import { AssetLoader } from './AssetLoader';
import { debugLog } from '@shared/utils/Logger';

// ---------- LPC layout constants ----------

export const LPC_FRAME_W = 64;
export const LPC_FRAME_H = 64;
export const LPC_COLS = 13;

// Oversized weapon frame size
const OVERSIZE_FRAME = 192;

/** LPC animation definitions: base row offset and frame count */
export interface LPCAnimDef {
  baseRow: number;    // first row of the 4-direction group
  frameCount: number; // how many columns are used
  loop: boolean;
}

export const LPC_ANIMS: Record<string, LPCAnimDef> = {
  spellcast: { baseRow: 0,  frameCount: 7,  loop: false },
  thrust:    { baseRow: 4,  frameCount: 8,  loop: false },
  walk:      { baseRow: 8,  frameCount: 9,  loop: true  },
  slash:     { baseRow: 12, frameCount: 6,  loop: false },
  shoot:     { baseRow: 16, frameCount: 13, loop: false },
  hurt:      { baseRow: 20, frameCount: 6,  loop: false },
};

/** Cardinal direction used by LPC sheets (4-dir) */
export type LPCDirection = 'up' | 'left' | 'down' | 'right';

/** Direction row offset inside a 4-row animation group */
const LPC_DIR_OFFSET: Record<LPCDirection, number> = {
  up:    0,
  left:  1,
  down:  2,
  right: 3,
};

/** Map 8-directional game facing to the nearest LPC 4-dir */
type Direction8 = 'up' | 'down' | 'left' | 'right' | 'up-left' | 'up-right' | 'down-left' | 'down-right';

const DIR8_TO_LPC: Record<Direction8, LPCDirection> = {
  'down':       'down',
  'down-left':  'left',
  'left':       'left',
  'up-left':    'up',
  'up':         'up',
  'up-right':   'up',
  'right':      'right',
  'down-right': 'right',
};

/** Map game animation states to LPC animation names */
export type GameAnimState = 'idle' | 'walk' | 'attack' | 'cast' | 'shoot' | 'hurt';

const GAME_STATE_TO_LPC: Record<GameAnimState, string> = {
  idle:   'walk',      // idle = walk frame 0
  walk:   'walk',
  attack: 'slash',
  cast:   'spellcast',
  shoot:  'shoot',
  hurt:   'hurt',
};

// ---------- Layer definition ----------

export interface LPCSpriteLayer {
  /** AssetLoader key for the loaded image */
  imageKey: string;
  /** Optional tint color (CSS color string) to apply via source-atop */
  tint?: string;
  /** Tint opacity (0-1). Default 0.35 */
  tintAlpha?: number;
  /**
   * If true, this layer uses oversized 192×192 frames (1152×768 sheet).
   * Only slash animation exists; other animations use fallbackKey.
   */
  oversize?: boolean;
  /** Standard-format weapon key for non-slash animations when oversize=true */
  fallbackKey?: string;
}

// ---------- Oversized weapon registry ----------

const OVERSIZED_WEAPONS = new Set([
  'lpc_weapon_longsword',
  'lpc_weapon_mace',
  'lpc_weapon_saber',
  'lpc_weapon_rapier',
  'lpc_weapon_waraxe_slash',
]);

/** Get a standard-format fallback key for oversized weapons */
function getOversizeFallback(key: string): string {
  switch (key) {
    case 'lpc_weapon_mace':
      return 'lpc_weapon_spear';
    case 'lpc_weapon_waraxe_slash':
      return 'lpc_weapon_waraxe';
    default:
      // longsword, saber, rapier → spear (closest standard melee)
      return 'lpc_weapon_spear';
  }
}

// ---------- Composite cache ----------

interface CacheEntry {
  canvas: HTMLCanvasElement;
  lastUsed: number;
}

// ---------- Main class ----------

export class LPCSpriteRenderer {
  private assetLoader: AssetLoader;
  private cache = new Map<string, CacheEntry>();
  private maxCacheSize = 512;

  // Reusable offscreen canvases for compositing (avoid GC churn)
  private compositeCanvas: HTMLCanvasElement;
  private compositeCtx: CanvasRenderingContext2D;
  private tintCanvas: HTMLCanvasElement;
  private tintCtx: CanvasRenderingContext2D;

  constructor(assetLoader: AssetLoader) {
    this.assetLoader = assetLoader;

    this.compositeCanvas = document.createElement('canvas');
    this.compositeCanvas.width = LPC_FRAME_W;
    this.compositeCanvas.height = LPC_FRAME_H;
    this.compositeCtx = this.compositeCanvas.getContext('2d')!;

    this.tintCanvas = document.createElement('canvas');
    this.tintCanvas.width = LPC_FRAME_W;
    this.tintCanvas.height = LPC_FRAME_H;
    this.tintCtx = this.tintCanvas.getContext('2d')!;
  }

  // ---------- Public API ----------

  /**
   * Draw composited LPC sprite layers onto the target context.
   *
   * @param ctx        - destination canvas context
   * @param layers     - ordered list of sprite layers (bottom to top)
   * @param animState  - game animation state
   * @param direction  - 8-directional facing
   * @param frameIndex - animation frame index (will be clamped)
   * @param x          - destination X (center-bottom of sprite)
   * @param y          - destination Y (center-bottom of sprite)
   * @param w          - destination width
   * @param h          - destination height
   */
  drawComposite(
    ctx: CanvasRenderingContext2D,
    layers: LPCSpriteLayer[],
    animState: GameAnimState,
    direction: Direction8,
    frameIndex: number,
    x: number,
    y: number,
    w: number,
    h: number
  ): boolean {
    const lpcAnim = GAME_STATE_TO_LPC[animState];
    const animDef = LPC_ANIMS[lpcAnim];
    if (!animDef) return false;

    const lpcDir = DIR8_TO_LPC[direction];
    const clampedFrame = animState === 'idle'
      ? 0
      : Math.min(frameIndex, animDef.frameCount - 1);

    // Build cache key from layer keys + tints + anim params
    const cacheKey = this.buildCacheKey(layers, lpcAnim, lpcDir, clampedFrame);

    let entry = this.cache.get(cacheKey);
    if (!entry) {
      // Composite layers onto offscreen canvas
      const composite = this.compositeFrame(layers, lpcAnim, lpcDir, clampedFrame);
      if (!composite) return false;

      // Store in cache
      const cached = document.createElement('canvas');
      cached.width = LPC_FRAME_W;
      cached.height = LPC_FRAME_H;
      cached.getContext('2d')!.drawImage(composite, 0, 0);
      entry = { canvas: cached, lastUsed: performance.now() };
      this.cache.set(cacheKey, entry);

      // Evict old entries if cache is too large
      if (this.cache.size > this.maxCacheSize) {
        this.evictOldest();
      }
    }
    entry.lastUsed = performance.now();

    // Draw to destination: position so (x, y) is center-bottom
    ctx.drawImage(entry.canvas, x - w / 2, y - h, w, h);
    return true;
  }

  /**
   * Get the frame count for a given game animation state.
   */
  getFrameCount(animState: GameAnimState): number {
    const lpcAnim = GAME_STATE_TO_LPC[animState];
    return LPC_ANIMS[lpcAnim]?.frameCount ?? 1;
  }

  /**
   * Check if the base body layer is loaded (minimum requirement for sprite rendering).
   * Missing optional layers (armor, weapon, etc.) are simply skipped during compositing.
   */
  areLayersLoaded(layers: LPCSpriteLayer[]): boolean {
    if (layers.length === 0) return false;
    // At minimum the first layer (body) must be loaded
    return !!this.assetLoader.getImage(layers[0].imageKey);
  }

  /**
   * Clear the composited frame cache (call on equipment change, etc.)
   */
  clearCache(): void {
    this.cache.clear();
  }

  // ---------- Internals ----------

  private buildCacheKey(
    layers: LPCSpriteLayer[],
    anim: string,
    dir: LPCDirection,
    frame: number
  ): string {
    // Layer keys + tints form the equipment signature
    let key = '';
    for (const l of layers) {
      key += l.imageKey;
      if (l.tint) key += l.tint;
      if (l.oversize) key += 'O';
      if (l.fallbackKey) key += l.fallbackKey;
      key += '|';
    }
    key += `${anim}_${dir}_${frame}`;
    return key;
  }

  private compositeFrame(
    layers: LPCSpriteLayer[],
    anim: string,
    dir: LPCDirection,
    frame: number
  ): HTMLCanvasElement | null {
    const cCtx = this.compositeCtx;
    cCtx.clearRect(0, 0, LPC_FRAME_W, LPC_FRAME_H);

    const animDef = LPC_ANIMS[anim];
    if (!animDef) return null;

    const dirOffset = LPC_DIR_OFFSET[dir];
    // Standard 64x64 source rect
    const stdSx = frame * LPC_FRAME_W;
    const stdSy = (animDef.baseRow + dirOffset) * LPC_FRAME_H;

    let anyDrawn = false;

    for (const layer of layers) {
      let img: HTMLImageElement | HTMLCanvasElement | undefined;
      let sx: number;
      let sy: number;

      if (layer.oversize) {
        if (anim === 'slash') {
          // Oversized weapon: 192x192 frames, slash only
          // Extract center 64x64 from the 192x192 frame
          img = this.assetLoader.getImage(layer.imageKey);
          const pad = (OVERSIZE_FRAME - LPC_FRAME_W) / 2; // 64
          sx = frame * OVERSIZE_FRAME + pad;
          sy = dirOffset * OVERSIZE_FRAME + pad;
        } else {
          // Non-slash: use standard fallback weapon
          img = layer.fallbackKey
            ? this.assetLoader.getImage(layer.fallbackKey)
            : undefined;
          sx = stdSx;
          sy = stdSy;
        }
      } else {
        img = this.assetLoader.getImage(layer.imageKey);
        sx = stdSx;
        sy = stdSy;
      }

      if (!img) continue;

      // Bounds check: skip if source rect exceeds image
      if (sx + LPC_FRAME_W > img.width || sy + LPC_FRAME_H > img.height) continue;

      if (layer.tint) {
        // Draw layer frame to tint canvas, then apply tint via source-atop
        const tCtx = this.tintCtx;
        tCtx.clearRect(0, 0, LPC_FRAME_W, LPC_FRAME_H);
        tCtx.globalCompositeOperation = 'source-over';
        tCtx.drawImage(img, sx, sy, LPC_FRAME_W, LPC_FRAME_H, 0, 0, LPC_FRAME_W, LPC_FRAME_H);
        tCtx.globalCompositeOperation = 'source-atop';
        tCtx.globalAlpha = layer.tintAlpha ?? 0.35;
        tCtx.fillStyle = layer.tint;
        tCtx.fillRect(0, 0, LPC_FRAME_W, LPC_FRAME_H);
        tCtx.globalAlpha = 1;
        tCtx.globalCompositeOperation = 'source-over';

        cCtx.drawImage(this.tintCanvas, 0, 0);
      } else {
        cCtx.drawImage(img, sx, sy, LPC_FRAME_W, LPC_FRAME_H, 0, 0, LPC_FRAME_W, LPC_FRAME_H);
      }
      anyDrawn = true;
    }

    return anyDrawn ? this.compositeCanvas : null;
  }

  private evictOldest(): void {
    // Remove the 25% oldest entries
    const entries = [...this.cache.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const removeCount = Math.floor(this.maxCacheSize * 0.25);
    for (let i = 0; i < removeCount && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  // ---------- Static helpers ----------

  /**
   * Preload all standard LPC layer images via AssetLoader.
   * Returns a promise that resolves when all images are loaded (or have failed).
   */
  static async preloadLayers(assetLoader: AssetLoader): Promise<void> {
    const basePath = 'sprites/lpc';
    const layers = [
      // Body
      { key: 'lpc_body_male',       src: `${basePath}/body/male/light.png` },
      // Legs
      { key: 'lpc_legs_pants',       src: `${basePath}/legs/pants/male/white_pants_male.png` },
      // Torso armor variants
      { key: 'lpc_torso_leather',    src: `${basePath}/torso/leather/male/chest_male.png` },
      { key: 'lpc_torso_leather_shoulders', src: `${basePath}/torso/leather/male/shoulders_male.png` },
      { key: 'lpc_torso_chain',      src: `${basePath}/torso/chain/male/mail_male.png` },
      { key: 'lpc_torso_chain_jacket', src: `${basePath}/torso/chain/male/jacket_male.png` },
      { key: 'lpc_torso_plate',      src: `${basePath}/torso/plate/male/chest_male.png` },
      { key: 'lpc_torso_plate_arms', src: `${basePath}/torso/plate/male/arms_male.png` },
      { key: 'lpc_torso_robe',      src: `${basePath}/torso/robe/male/robe_purple.png` },
      // Hair
      { key: 'lpc_hair_brown',       src: `${basePath}/hair/male/plain/brown.png` },
      // Helmets
      { key: 'lpc_helm_metal',       src: `${basePath}/head/helms/male/metal.png` },
      { key: 'lpc_helm_spangenhelm', src: `${basePath}/head/helms/male/spangenhelm_steel.png` },
      { key: 'lpc_helm_chain',       src: `${basePath}/head/helms/male/chain_steel.png` },
      { key: 'lpc_helm_nasal',       src: `${basePath}/head/helms/male/nasal_steel.png` },
      { key: 'lpc_helm_hood_leather', src: `${basePath}/head/helms/male/hood_leather.png` },
      { key: 'lpc_helm_hood_brown',  src: `${basePath}/head/helms/male/hood_brown.png` },
      // Weapons — standard format (832x1344, 64x64 frames)
      { key: 'lpc_weapon_spear',     src: `${basePath}/weapons/right_hand/male/spear_male.png` },
      { key: 'lpc_weapon_staff',     src: `${basePath}/weapons/right_hand/male/woodwand_male.png` },
      { key: 'lpc_weapon_bow',       src: `${basePath}/weapons/right_hand/male/bow_male.png` },
      { key: 'lpc_weapon_dagger',    src: `${basePath}/weapons/right_hand/male/dagger_male.png` },
      // Weapons — axe (standard + oversized slash)
      { key: 'lpc_weapon_waraxe',    src: `${basePath}/weapons/right_hand/male/waraxe_male.png` },
      { key: 'lpc_weapon_waraxe_slash', src: `${basePath}/weapons/right_hand/male/waraxe_slash_male.png` },
      // Weapons — staves
      { key: 'lpc_weapon_staff_gnarled', src: `${basePath}/weapons/right_hand/male/staff_gnarled_male.png` },
      { key: 'lpc_weapon_staff_crystal', src: `${basePath}/weapons/right_hand/male/staff_crystal_male.png` },
      // Weapons — oversized format (1152x768, 192x192 frames, slash only)
      { key: 'lpc_weapon_longsword', src: `${basePath}/weapons/right_hand/male/longsword_male.png` },
      { key: 'lpc_weapon_mace',      src: `${basePath}/weapons/right_hand/male/mace_male.png` },
      { key: 'lpc_weapon_saber',     src: `${basePath}/weapons/right_hand/male/saber_male.png` },
      { key: 'lpc_weapon_rapier',    src: `${basePath}/weapons/right_hand/male/rapier_male.png` },
      // Shields
      { key: 'lpc_shield',          src: `${basePath}/weapons/left_hand/male/shield_male_cutoutforbody.png` },
      { key: 'lpc_shield_round',    src: `${basePath}/weapons/left_hand/male/shield_round_brown.png` },
      { key: 'lpc_shield_kite',     src: `${basePath}/weapons/left_hand/male/shield_kite_gray.png` },
      // Feet
      { key: 'lpc_boots_brown',      src: `${basePath}/feet/shoes/male/brown_shoes_male.png` },
      // Cape
      { key: 'lpc_cape',            src: `${basePath}/cape/cape_brown.png` },
      // Hands/gloves
      { key: 'lpc_gloves_leather',  src: `${basePath}/hands/gloves/male/leather_bracers_male.png` },
      { key: 'lpc_gloves_metal',    src: `${basePath}/hands/gloves/male/metal_gloves_male.png` },
      // Belt
      { key: 'lpc_belt',            src: `${basePath}/belt/male/leather_male.png` },
      { key: 'lpc_belt_cloth',      src: `${basePath}/belt/male/white_cloth_male.png` },
      // Quiver (behind body, for archers)
      { key: 'lpc_quiver',          src: `${basePath}/cape/quiver.png` },
      // Additional boots
      { key: 'lpc_boots_leather',   src: `${basePath}/feet/shoes/male/leather_boots_male.png` },
      { key: 'lpc_boots_plate',     src: `${basePath}/feet/shoes/male/plate_steel_boots_male.png` },
      { key: 'lpc_boots_metal',     src: `${basePath}/feet/shoes/male/metal_boots_male.png` },
      { key: 'lpc_boots_golden',    src: `${basePath}/feet/shoes/male/golden_boots_male.png` },
      { key: 'lpc_boots_black',     src: `${basePath}/feet/shoes/male/black_shoes_male.png` },
      // Additional gloves
      { key: 'lpc_gloves_golden',   src: `${basePath}/hands/gloves/male/golden_gloves_male.png` },
      // Cape color variants
      { key: 'lpc_cape_blue',       src: `${basePath}/cape/cape_blue.png` },
      { key: 'lpc_cape_red',        src: `${basePath}/cape/cape_red.png` },
      { key: 'lpc_cape_purple',     src: `${basePath}/cape/cape_lavender.png` },
      { key: 'lpc_cape_yellow',     src: `${basePath}/cape/cape_yellow.png` },
      { key: 'lpc_cape_black',      src: `${basePath}/cape/cape_black.png` },
      { key: 'lpc_cape_white',      src: `${basePath}/cape/cape_white.png` },
      // Robe color variants
      { key: 'lpc_robe_blue',       src: `${basePath}/torso/robe/male/robe_blue.png` },
      { key: 'lpc_robe_red',        src: `${basePath}/torso/robe/male/robe_red.png` },
      { key: 'lpc_robe_black',      src: `${basePath}/torso/robe/male/robe_black.png` },
      { key: 'lpc_robe_white',      src: `${basePath}/torso/robe/male/robe_white.png` },
      { key: 'lpc_robe_forest',     src: `${basePath}/torso/robe/male/robe_forest_green.png` },
    ];

    const results = await Promise.allSettled(
      layers.map(({ key, src }) => assetLoader.loadImage(key, src))
    );

    let loaded = 0;
    let failed = 0;
    for (const r of results) {
      if (r.status === 'fulfilled') loaded++;
      else failed++;
    }
    debugLog('LPC', `Preloaded ${loaded} LPC layers, ${failed} failed`);
  }

  /**
   * Build layer list for a character based on class name and equipped items.
   */
  static buildLayersForPlayer(opts: {
    className: string;
    chestStyle?: string;
    chestTint?: string;
    headEquipped: boolean;
    headTint?: string;
    weaponName?: string;
    weaponTint?: string;
    bootsEquipped: boolean;
    bootsTint?: string;
    glovesEquipped: boolean;
    glovesTint?: string;
    capeEquipped: boolean;
    capeTint?: string;
    beltEquipped: boolean;
    offhandEquipped: boolean;
    // Defense type per slot → sprite variant
    bootsDefense?: string;
    glovesDefense?: string;
    headDefense?: string;
    chestDefense?: string;
    // Rarity → cape/robe color
    capeRarity?: string;
    chestRarity?: string;
  }): LPCSpriteLayer[] {
    const layers: LPCSpriteLayer[] = [];

    // 1. Cape/quiver behind body
    if (opts.capeEquipped) {
      const capeKey = getCapeKeyByRarity(opts.capeRarity);
      const cape: LPCSpriteLayer = { imageKey: capeKey };
      if (opts.capeTint) { cape.tint = opts.capeTint; cape.tintAlpha = 0.45; }
      layers.push(cape);
    } else if (opts.className === 'mergen') {
      // Mergen gets a quiver behind body by default
      layers.push({ imageKey: 'lpc_quiver' });
    }

    // 2. Base body (always present)
    layers.push({ imageKey: 'lpc_body_male' });

    // 3. Legs / pants
    layers.push({ imageKey: 'lpc_legs_pants' });

    // 4. Boots (if equipped) — defense type determines sprite variant
    if (opts.bootsEquipped) {
      const bootsKey = getBootsKeyByDefense(opts.bootsDefense);
      const boots: LPCSpriteLayer = { imageKey: bootsKey };
      if (opts.bootsTint) { boots.tint = opts.bootsTint; boots.tintAlpha = 0.45; }
      layers.push(boots);
    }

    // 5. Belt (if equipped)
    if (opts.beltEquipped) {
      layers.push({ imageKey: 'lpc_belt' });
    }

    // 6. Torso armor — defense type determines style, rarity determines robe color
    const armorStyle = opts.chestStyle || getChestStyleByDefense(opts.chestDefense, opts.className);
    const armorLayers = getArmorLayers(armorStyle, opts.chestRarity);
    for (const key of armorLayers) {
      const armorLayer: LPCSpriteLayer = { imageKey: key };
      if (opts.chestTint) { armorLayer.tint = opts.chestTint; armorLayer.tintAlpha = 0.5; }
      layers.push(armorLayer);
    }

    // 7. Gloves (if equipped) — defense type determines sprite variant
    if (opts.glovesEquipped) {
      const glovesKey = getGlovesKeyByDefense(opts.glovesDefense);
      const gloves: LPCSpriteLayer = { imageKey: glovesKey };
      if (opts.glovesTint) { gloves.tint = opts.glovesTint; gloves.tintAlpha = 0.45; }
      layers.push(gloves);
    }

    // 8. Hair — shown when no helmet (hoods cover hair)
    const showHair = !opts.headEquipped;
    if (showHair) {
      const hair: LPCSpriteLayer = { imageKey: 'lpc_hair_brown' };
      const hairTint = getClassHairTint(opts.className);
      if (hairTint) { hair.tint = hairTint; hair.tintAlpha = 0.5; }
      layers.push(hair);
    }

    // 9. Helmet — defense type determines sprite variant; light headgear = hood
    if (opts.headEquipped) {
      if (isLightHeadgear(opts.headDefense)) {
        // Evasion/ES → hood (drawn over hair)
        const hoodKey = opts.headDefense === 'energyShield' ? 'lpc_helm_hood_brown' : 'lpc_helm_hood_leather';
        const hood: LPCSpriteLayer = { imageKey: hoodKey };
        if (opts.headTint) { hood.tint = opts.headTint; hood.tintAlpha = 0.4; }
        layers.push(hood);
      } else {
        // Heavy armour → metal/chain/nasal helm
        const helmKey = getHelmKeyByDefense(opts.headDefense);
        const helm: LPCSpriteLayer = { imageKey: helmKey };
        if (opts.headTint) { helm.tint = opts.headTint; helm.tintAlpha = 0.4; }
        layers.push(helm);
      }
    }

    // 10. Shield (offhand)
    if (opts.offhandEquipped) {
      layers.push({ imageKey: 'lpc_shield' });
    }

    // 11. Weapon
    const weaponKey = getWeaponLayerKey(opts.className, opts.weaponName);
    const isOversized = OVERSIZED_WEAPONS.has(weaponKey);
    const weaponLayer: LPCSpriteLayer = { imageKey: weaponKey };
    if (isOversized) {
      weaponLayer.oversize = true;
      weaponLayer.fallbackKey = getOversizeFallback(weaponKey);
    }
    if (opts.weaponTint) { weaponLayer.tint = opts.weaponTint; weaponLayer.tintAlpha = 0.4; }
    layers.push(weaponLayer);

    return layers;
  }
}

// ---------- Equipment → layer key helpers ----------

/** Get class-specific default armor style when nothing special is equipped */
function getClassDefaultArmor(className: string): string {
  switch (className) {
    case 'kam':    return 'cloth';   // Kam wears robes
    case 'mergen': return 'leather'; // Mergen wears leather
    case 'boru':   return 'leather'; // Boru wears light leather (wolf warrior)
    case 'alp':
    default:       return 'leather'; // Alp wears leather (upgrades to chain/plate)
  }
}

/** Map defense type to chest armor style */
function getChestStyleByDefense(defenseType?: string, className?: string): string {
  if (!defenseType) return getClassDefaultArmor(className || 'alp');
  if (defenseType.includes('armour') && defenseType.includes('evasion')) return 'chain';
  if (defenseType.includes('armour') && defenseType.includes('energyShield')) return 'chain';
  switch (defenseType) {
    case 'armour':       return 'plate';
    case 'evasion':      return 'leather';
    case 'energyShield': return 'cloth';
    default:             return getClassDefaultArmor(className || 'alp');
  }
}

/** Return ordered list of armor layer keys for a given style.
 *  Some styles use multiple overlapping LPC layers for richer visuals. */
function getArmorLayers(style: string, rarity?: string): string[] {
  switch (style) {
    case 'chain':
      return ['lpc_torso_chain', 'lpc_torso_chain_jacket'];
    case 'plate':
      return ['lpc_torso_plate', 'lpc_torso_plate_arms'];
    case 'magical':
      return ['lpc_torso_chain', 'lpc_torso_chain_jacket'];
    case 'legendary':
      return ['lpc_torso_plate', 'lpc_torso_plate_arms'];
    case 'cloth':
      return [getRobeKeyByRarity(rarity)];
    case 'leather':
    default:
      return ['lpc_torso_leather', 'lpc_torso_leather_shoulders'];
  }
}

/** Map rarity to boots sprite key based on defense type */
function getBootsKeyByDefense(defenseType?: string): string {
  if (!defenseType) return 'lpc_boots_brown';
  if (defenseType.includes('armour'))       return 'lpc_boots_metal';
  if (defenseType.includes('energyShield')) return 'lpc_boots_black';
  return 'lpc_boots_brown'; // evasion = default leather
}

/** Map rarity to gloves sprite key based on defense type */
function getGlovesKeyByDefense(defenseType?: string): string {
  if (!defenseType) return 'lpc_gloves_leather';
  if (defenseType.includes('armour'))       return 'lpc_gloves_metal';
  if (defenseType.includes('energyShield')) return 'lpc_gloves_golden';
  return 'lpc_gloves_leather'; // evasion = default leather
}

/** Map defense type to helmet sprite key */
function getHelmKeyByDefense(defenseType?: string): string {
  if (!defenseType) return 'lpc_helm_metal';
  if (defenseType.includes('armour') && defenseType.includes('evasion')) return 'lpc_helm_chain';
  if (defenseType.includes('armour') && defenseType.includes('energyShield')) return 'lpc_helm_nasal';
  switch (defenseType) {
    case 'armour':       return 'lpc_helm_spangenhelm';
    default:             return 'lpc_helm_metal';
  }
}

/** Evasion/ES headgear is light — show hair instead of bulky helmet */
function isLightHeadgear(defenseType?: string): boolean {
  if (!defenseType) return false;
  return defenseType === 'evasion' || defenseType === 'energyShield'
    || defenseType === 'evasion_energyShield';
}

/** Map cape rarity to cape sprite key */
function getCapeKeyByRarity(rarity?: string): string {
  switch (rarity) {
    case 'magic':     return 'lpc_cape_blue';
    case 'rare':      return 'lpc_cape_red';
    case 'set':       return 'lpc_cape_purple';
    case 'legendary': return 'lpc_cape_yellow';
    default:          return 'lpc_cape'; // normal = brown
  }
}

/** Map robe rarity to robe sprite key */
function getRobeKeyByRarity(rarity?: string): string {
  switch (rarity) {
    case 'magic':     return 'lpc_robe_blue';
    case 'rare':      return 'lpc_robe_red';
    case 'set':       return 'lpc_robe_black';
    case 'legendary': return 'lpc_robe_white';
    default:          return 'lpc_torso_robe'; // normal = purple
  }
}

/** Get class-specific hair tint color (null = no tint, use default brown) */
function getClassHairTint(className: string): string | null {
  switch (className) {
    case 'kam':    return '#1A1A2E'; // Very dark (almost black) for shaman
    case 'mergen': return '#8B4513'; // Reddish-brown for ranger
    case 'boru':   return '#808080'; // Silver-gray for wolf warrior
    case 'alp':
    default:       return null;      // Default brown, no tint
  }
}

/** Map equipped weapon name to LPC weapon layer key */
function getWeaponLayerKey(className: string, weaponName?: string): string {
  if (weaponName) {
    const lower = weaponName.toLowerCase();
    // Bow-type weapons
    if (lower.includes('yay')) return 'lpc_weapon_bow';
    // Staff-type weapons
    if (lower.includes('asa') || lower.includes('değnek')) return 'lpc_weapon_staff';
    // Axe-type weapons → waraxe (standard + oversized slash)
    if (lower.includes('balta')) return 'lpc_weapon_waraxe_slash';
    // Sword-type weapons → longsword (oversized, with spear fallback)
    if (lower.includes('kılıç') || lower.includes('kilic') || lower.includes('uzun'))
      return 'lpc_weapon_longsword';
    // Dagger-type
    if (lower.includes('hançer') || lower.includes('bıçak'))
      return 'lpc_weapon_dagger';
    // Spear-type
    if (lower.includes('mızrak') || lower.includes('mizrak'))
      return 'lpc_weapon_spear';
  }
  // Fallback: class default
  switch (className) {
    case 'kam':    return 'lpc_weapon_staff';
    case 'mergen': return 'lpc_weapon_bow';
    case 'boru':   return 'lpc_weapon_dagger';  // Claws/dagger for wolf warrior
    case 'alp':
    default:       return 'lpc_weapon_longsword';
  }
}
